import { joinUrl, extend } from 'pexui';
import { get } from 'lodash';
import * as utils from '../utilities/utils';
import { beginWidgetSession, generateSessionToken, getRequestTracker } from '../utilities/secutil';
import * as consts from '../models/consts';
import globals from '../models/globals';
import { flow } from '../models/flow';

// currency hashmap
const currencyHashMap = {
  US: 'USD',
};

// communication with backend
const getDocuploadUri = () => {
  const wconfig = globals.widgetConfig;
  return (wconfig && wconfig.widget_service_url) || joinUrl(utils.getBaseUri(), '/acquisition/digital/v1/info_collector');
};

// communication with backend
const getDocuploadUriV2 = () => {
  const wconfig = globals.widgetConfig;
  return (wconfig && wconfig.widget_service_url) || joinUrl(utils.getBaseUri(), '/acquisition/digital/v2/info_collector');
};

const getDocuploadUploadUrl = () => (
  // getDocuploadUri().replace('/acquisition/digital/v1/info_collector', '/v1/shared/services/application/upload')
  joinUrl(getDocuploadUri(), '/document_upload')
);

const encodeAllSpcChars = str => (str ? str.replace(/[\W]/g, (c) => {
  const s = `00${c.charCodeAt(0).toString(16)}`;
  return `%${s.slice(-2)}`;
}) : '');

export const checkResponseCode = (respObj) => {
  // Check for null response
  if (respObj) {
    if (!(respObj.hasOwnProperty('response_code')
            && (respObj.response_code === consts.SUCCESS_CODE || respObj.response_code === consts.PARTIAL_SUCCESS_CODE))) {
      throw respObj;
    }
  }
};

const clearTidAndToken = (respObj, code) => {
  // Check for null response
  const respCode = get(respObj, 'response_code');
  const isAtLeastPartialSuccess = (respCode === consts.SUCCESS_CODE || respCode === consts.PARTIAL_SUCCESS_CODE);

  if (isAtLeastPartialSuccess) {
    // Clear cached TID and token by setting to empty strings on succesful submit
    flow.docUploadTids[code] = '';
    flow.docupld_token = '';
  }

  checkResponseCode(respObj);
};

const updateTidAndToken = (respObj, code) => {
  // Check for null response
  if (respObj) {
    // Set cached TID to the response TID if the response is successful
    if (typeof (respObj.response_code) !== 'undefined' && respObj.response_code === consts.SUCCESS_CODE) {
      if (respObj.docupld_tid) {
        flow.docUploadTids[code] = respObj.docupld_tid;
      }
    }
    // Set cached token to response token if it exists, otherwise set to empty string
    flow.docupld_token = respObj.docupld_token || '';
  }
  checkResponseCode(respObj);
};

/**
 * A common util to call REST service
 * @param {string} path relative path of the service
 * @param {object} headers additional request headers
 * @param {object} reqObj the request object being encoded in JSON and sent in the body
 * @param {object} opts optional arguments passed to ajax call.
 * @return {object} Promise
 */

const callRest = (path, headers, reqObj, opts) => {
  reqObj = reqObj || {};
  if (!reqObj.messageId && !reqObj.message_id) {
    reqObj.message_id = utils.randomString(12);
  }
  headers = headers || {};
  if (!headers.hasOwnProperty('Widget-Token')) {
    headers['Widget-Token'] = generateSessionToken();
  }
  headers['Content-Type'] = 'application/vnd.americanexpress.req-v1+json';
  headers['Request-Tracker'] = getRequestTracker(reqObj.message_id, flow.req.originator_id, flow.req.transaction_id);

  const promise = utils.ajaxPromise(extend({
    method: 'POST',
    url: joinUrl(getDocuploadUri(), path),
    headers,
    data: JSON.stringify(reqObj),
  }, opts))
    .then((resp) => {
      resp.obj = JSON.parse(resp.data);

      if (
        (reqObj.message_id && resp.obj.message_id !== reqObj.message_id)
            || (reqObj.messageId && resp.obj.messageId !== reqObj.messageId)
      ) {
        throw new Error('request/response mismatch');
      }
      return resp;
    });

  return promise;
};

const callRestV2 = (path, headers, reqObj, opts) => {
  reqObj = reqObj || {};
  if (!reqObj.messageId && !reqObj.message_id) {
    reqObj.message_id = utils.randomString(12);
  }
  headers = headers || {};
  if (!headers.hasOwnProperty('Widget-Token')) {
    headers['Widget-Token'] = generateSessionToken();
  }
  headers['Content-Type'] = 'application/vnd.americanexpress.req-v1+json';
  headers['Request-Tracker'] = getRequestTracker(reqObj.message_id, flow.req.originator_id, flow.req.transaction_id);

  const promise = utils.ajaxPromise(extend({
    method: 'POST',
    url: joinUrl(getDocuploadUriV2(), path),
    headers,
    data: JSON.stringify(reqObj),
  }, opts))
    .then(resp => resp);

  return promise;
};

export var DocUploadService = {
  /**
     * Initialize widget session
     * @return a Promise
     */
  createDocument: (widgetConfig, widgetRequest) => {
    const widgetRequest2 = extend({}, widgetRequest); // clone and modify the req before sending to service
    const vaultKey = widgetRequest.vault_key || '';
    // remove vault key data from front end
    delete widgetRequest2.vault_key;
    widgetRequest2.widget_config = widgetConfig;

    return callRest('/documents', { 'Widget-Token': vaultKey }, {
      widget_request: widgetRequest2,
    }).then((resp) => {
      checkResponseCode(resp.obj);

      const widgetSessionKey = resp.obj.widget_session_key || resp.headers['Widget-Session-Key'];
      beginWidgetSession(widgetSessionKey);
      // save the widget config from node side
      globals.config = resp.obj.config || {};

      return resp.obj;
    });
  },

  /**
     * @param {object} file the file to upload
     * @param {object} formFields  {transactionId, weneedCode, originatorId, market}
     * @return {object} Promise
     */
  upload: (file, formFields, code) => utils.uploadPromise({
    url: getDocuploadUploadUrl(),
    file,
    formFields,
    headers: {
      'Widget-Token': generateSessionToken(),
      'Request-Tracker': getRequestTracker(formFields.message_id, flow.req.originator_id, flow.req.transaction_id),
      'Personal-Info': formFields.personal_info,
    },
  }).then((resp) => {
    resp.obj = JSON.parse(resp.data);

    updateTidAndToken(resp.obj, code);
    return resp;
  }),

  /**
     * Confirm an uploaded file to DMR (by Upload button)
     * @param {object} reqObj
     * @return {object} Promise
     */
  confirmUpload: (reqObj, code) => callRest('/document_submit', null, reqObj)
    .then((resp) => {
      clearTidAndToken(resp.obj, code);

      return resp.obj;
    }),

  /**
     * Remove an attached file from DMR
     * @param {object} reqObj
     * @return {object} Promise
     */
  removeFile: (reqObj, code) => callRest('/document_remove', null, reqObj)
    .then((resp) => {
      updateTidAndToken(resp.obj, code);
      return resp.obj;
    }),

  /**
     * Save Yodlee Terms and Condition when user accepts
     * @param {string} transactionId
     * @param {string} transactionId
     * @param {string} pcn
     * @param {string} tncType
     * @param {string} tnc
     * @param {boolean} accepted
     * @return {object} a Promise
     */
  saveTnCAcceptance: (transactionId, originatorId, pcn, tncType, tnc, accepted) => callRest('/documents/tnc_acceptance', null, {
    transaction_id: transactionId,
    originator_id: originatorId,
    process_control_number: pcn,
    tnc_type: tncType,
    tnc_text: tnc,
    accepted,
  })
    .then((resp) => {
      checkResponseCode(resp.obj);
      return resp.obj;
    }),

  /**
     * Get Yodlee Attributes
     * @param {object} req the widget request
     * @return {object} Promise
     */
  getYodleeAttributes: (req) => {
    let reqObj;
    if (req.is_single_sign_on || req.is_sso) {
      reqObj = {
        transaction_id: req.transaction_id,
        originator_id: req.originator_id,
        is_sso_flow: true,
        market: req.market,
        business_unit_code: req.document_data.business_unit_code,
        account_number: req.account_number,
        use_case: req.document_data.offer_details_code,
        // security_token & public_guid will be added by the node layer getting values from the SSO cookies.
      };
    } else {
      reqObj = {
        transaction_id: req.transaction_id,
        originator_id: req.originator_id,
        is_sso_flow: false,
        market: req.market,
        business_unit_code: req.document_data.business_unit_code,
        personal_info: req.personal_info,
        use_case: req.document_data.offer_details_code,
      };
    }

    return callRest('/yodlee_attributes', null, reqObj, { withCredentials: true })
      .then((resp) => {
        checkResponseCode(resp.obj);
        const {
          is_recurr: isRecurr,
          provider_specifics: providerSpecifics,
          response_message: responseMessage,
          encrypted_yodlee_attribute: encryptedYodleeAttribute,
          additionalYodleeParams,
          saml,
        } = resp.obj;

        const yodleeAttributes = encryptedYodleeAttribute || saml;

        if (!yodleeAttributes) {
          throw new Error(responseMessage || 'yodlee error');
        }

        return { yodleeAttributes, isRecurr, providerSpecifics, additionalYodleeParams };
      });
  },

  /**
     * Check Account Mismatch with FG
     * @param {object} requestPayload
     * @param {object} yodleeResponse
     */
  validateAccountDetails: (yodleeResp, submitAttempts, isYsl) => {
    const { isRecurr, req } = flow;
    const {
      client_organization_id,
      document_data,
      personal_info,
      account_number,
      is_single_sign_on,
      originator_id,
      market,
      transaction_id,
    } = req;
    const {
      business_unit_code: businessCode,
      offer_details_code: usecase_name,
      documents_requested: documentsRequested,
      process_control_number,
    } = document_data;

    const companyData = businessCode === 'OPEN' || businessCode === 'GCP'
      ? { company_name: documentsRequested[0].organization_name }
      : null;

    const yslData = { guid: yodleeResp.GUID, sites: yodleeResp.sites };
    const yodleeData = {
      guid: yodleeResp.GUID,
      segment_name: yodleeResp.Segment,
      snapshot_required_indicator: yodleeResp.SNAPSHOT_REQUIRED,
      yodlee_xref_id: yodleeResp.appID,
      is_unregister: !isRecurr,
    };

    const yodleeRequestData = isYsl ? yslData : yodleeData;

    const requestObj = {
      account_number,
      account_business_unit_code: businessCode,
      client_organization_id,
      company_data: companyData,
      currency_code: currencyHashMap[market] || 'USD',
      country_code: market,
      is_single_sign_on,
      originator_id,
      personal_info,
      process_control_number,
      submit_attempts: submitAttempts,
      transaction_id,
      usecase_name,
      yodlee_data: yodleeRequestData,
      is_recurr: isRecurr,
    };

    // TODO - implement axios library instead of relying on pexui ajax function
    // so we can handle node response object with appropriate code
    return callRest('/details/validate', null, requestObj)
      .then(res => JSON.parse(res.data));
  },

  /**
     * Save Yodlee Response
     * @param {string} transactionId
     * @param {object} yodleeResponse
     * @return {object} Promise
     */
  saveYodleeResponse: (transactionId, yodleeResponse) => callRest('/documents/yodlee_response', null, {
    transaction_id: transactionId,
    yodlee_response: yodleeResponse,
  })
    .then(res => res.obj)
    .catch(err => console.log('Fail to save Yodlee response with the following error:', err)),

  /**
     * Get DocuSign URL
     * @param {Object} req widgetRequest
     * @param {String} returnURL return URL
     * @return {object} Promise
     * resp object:
     *   envelope_id
     *   docusign_url
     */
  getDocuSignUrl: (req, returnURL) => callRest('/docusign/url', null, {
    transaction_id: req.transaction_id,
    originator_id: 'getinfowidget',
    is_sso_flow: req.is_single_sign_on,
    account_number: req.account_number,
    is_small_business: req.is_small_business || false,
    segment_name: req.segment_name || 'Retail',
    snapshot_required: req.snapshot_required || false,
    process_control_number: req.document_data.process_control_number,
    return_url: returnURL,
  }, { withCredentials: true })
    .then((resp) => {
      checkResponseCode(resp.obj);
      return resp.obj;
    }),

  /**
     * Get FG OptIn status
     * @param {object} req the widget request
     * @return {object} Promise
     */
  getOptInStatus: req => callRest('/optin/status', null, {
    account_number: req.account_number,
    country_code: req.market,
    usecase_name: req.document_data.offer_details_code,
    transaction_id: req.transaction_id,
  })
    .then(response => response.obj)
    .catch((error) => {
      throw new Error(`!${utils.convertError(error).message}`);
    }),

  /**
      * Make log to server-side
      * @param {object} req
      */
  log2Server: req => callRestV2('/log', null, req).catch((error) => {
    console.log(error);
  }),
};



// WEBPACK FOOTER //
// ./application/src/services/docuploadsvc.js